import { CommonModule } from '@angular/common';
import { Component, Inject, type OnDestroy, type OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  MAT_SNACK_BAR_DATA,
  MatSnackBarModule,
  MatSnackBarRef,
} from '@angular/material/snack-bar';

export interface ErrorSnackbarData {
  message: string;
  duration?: number;
}

export const isErrorSnackbarData = (data: ErrorSnackbarData | string): data is ErrorSnackbarData => {
  if (
    typeof data === 'object'
      && Object.hasOwn(data, 'message')
      && typeof data.message === 'string'
  ) {
    return true;
  }
  return false;
}

const DEFAULT_ERROR_DURATION = 10000; // 10 seconds

@Component({
  selector: 'cdw-error-snackbar',
  template: `
    <div class="error-snackbar">
      <mat-icon>error</mat-icon>
      <span>{{ message }}</span>
      <button mat-icon-button (click)="dismiss()">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  `,
  styles: [
    `
    .error-snackbar {
      display: flex;
      align-items: center;
      gap: 8px;
      color: white;
    }
    .error-snackbar mat-icon:first-child {
      color: #f44336;
    }
    .error-snackbar span {
      flex: 1;
    }
  `,
  ],
  standalone: true,
  imports: [CommonModule, MatSnackBarModule, MatButtonModule, MatIconModule],
})
export class ErrorSnackbarComponent implements OnInit, OnDestroy {
  private autoDismissTimer: ReturnType<typeof setTimeout> | null = null;
  message: string;

  constructor(
    public snackBarRef: MatSnackBarRef<ErrorSnackbarComponent>,
    @Inject(MAT_SNACK_BAR_DATA) public data: ErrorSnackbarData | string
  ) {
    // Support both string and object data formats for backward compatibility
    this.message = String(data);
    if (isErrorSnackbarData(data)) {
      this.message = data.message;
    }
  }

  ngOnInit(): void {
    const duration =
      typeof this.data === 'string'
        ? DEFAULT_ERROR_DURATION
        : (this.data.duration ?? DEFAULT_ERROR_DURATION);

    this.autoDismissTimer = setTimeout(() => this.dismiss(), duration);
  }

  ngOnDestroy(): void {
    if (this.autoDismissTimer) {
      clearTimeout(this.autoDismissTimer);
      this.autoDismissTimer = null;
    }
  }

  dismiss(): void {
    this.snackBarRef.dismiss();
  }
}
